<script setup lang="ts">
import DropdownGroup from '@/components/forms/groups/DropdownGroup.vue';
import InputTextGroup from '@/components/forms/groups/InputTextGroup.vue';
import CalendarGroup from '@/components/forms/groups/CalendarGroup.vue';
import AutoCompleteAddress from '@/components/AutoCompleteAddress.vue';
import OnboardingInvitedButtons from './components/OnboardingInvitedButtons.vue';

import { useForm } from 'vee-validate';
import { yup, citizenshipIsTaxIdCountry } from '@clearbanc/validation';

import { useStore } from 'vuex';
import { computed, ref } from 'vue';

import {
  unsanctionedCountryOptionsWithPriority,
  isCountryWithPostalCode,
} from '@/data/country-code-with-names';
import { COUNTRY_CODES } from '@/data/supported-country-codes';
import { CountryCodes } from '@clearbanc/data-common-types';
import {
  personalTaxIdLabelShort,
  postalCodeLabel,
  stateLabel,
  stateOptionsForCountry,
} from '@/utils/local';
import { parsePlaceData, PlaceDataOutput } from '@/utils/address';
import { RequestStatus } from '@/types/request.types';
import { format, isDate, parse } from 'date-fns';
import analytics, { SEGMENT_EVENTS } from '@/utils/analytics';
import { PatchUserDto, UserResponseDto } from '@clearbanc/baus-sdk';
import { useProfiling } from '@/composables/useProfiling';
import { RouteNames } from '@/data/routes';
import { EVENT_TYPES } from '@/data/lexisnexis-types';

// -----
// Variables
// -----

const store = useStore();
const profiling = useProfiling(RouteNames.ONBOARDING.INVITED.VERIFICATION, {
  type: EVENT_TYPES.ACCOUNT_CREATION,
});

const currentUser = computed<UserResponseDto>(() => {
  return store.getters.user;
});

const showErrorMessage = ref(false);
const errorMessage = ref('');

const updateUserRequestStatus = computed<RequestStatus>(() => {
  return store.getters.requestStatus('UPDATE_USER');
});

// -----
// Form Setup
// -----

interface FormValues {
  citizenship?: string;
  personalTaxId?: string;
  passportNumber?: string;
  birthday: string;
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
}

const validationSchema = yup.object({
  citizenship: yup.string(),
  personalTaxId: yup.ssn('citizenship'),
  passportNumber: yup.passport('citizenship'),
  birthday: yup.birthday(),
  line1: yup.address(),
  line2: yup.address().notRequired(),
  city: yup.city(),
  state: yup.string(),
  country: yup.string(),
  postalCode: yup.postalCode(),
});

const { values, setValues, meta } = useForm<FormValues>({
  validationSchema,
  initialValues: {
    citizenship: currentUser.value.citizenship,
    personalTaxId: '',
    passportNumber: '',
    birthday: currentUser.value.birthday,
    line1: currentUser.value.address?.line1,
    line2: currentUser.value.address?.line2,
    city: currentUser.value.address?.city,
    state: currentUser.value.address?.state,
    country: currentUser.value.address?.country,
    postalCode: currentUser.value.address?.postalCode,
  },
});

const hasErrors = computed(() => !meta.value.valid);

// ----
// Constants
// ----

const COUNTRIES = unsanctionedCountryOptionsWithPriority;

// ----
// Methods
// ----

const onCitizenshipChange = () => {
  setValues({
    ...values,
    personalTaxId: '',
    passportNumber: '',
  });
};

const handleAddressSelected = (placeData: PlaceDataOutput) => {
  const parsedData = parsePlaceData(placeData);

  setValues({
    ...values,
    ...parsedData,
    state: parsedData.state?.value,
    country: parsedData.country?.value,
  });
};

const handleNext = async (): Promise<boolean> => {
  // Logic to user verification here
  const formValues = values;
  showErrorMessage.value = false;

  const birthdayDate = isDate(formValues.birthday)
    ? formValues.birthday
    : parse(formValues.birthday, 'yyyy-MM-dd', new Date());

  const payload: PatchUserDto = {
    citizenship: formValues.citizenship,
    birthday: format(birthdayDate as Date, 'yyyy-MM-dd'),
    address: {
      line1: formValues.line1,
      line2: formValues.line2,
      city: formValues.city,
      state: formValues.state,
      country: formValues.country,
      postalCode: formValues.postalCode,
    },
  };

  // Only include personalTaxId for US/CA/UK
  if (
    [COUNTRY_CODES.US, COUNTRY_CODES.CA, COUNTRY_CODES.GB].includes(
      formValues.citizenship as CountryCodes,
    )
  ) {
    payload.personalTaxId = formValues.personalTaxId;
  } else {
    // Only include passportNumber for non-US/CA/UK countries
    payload.passportNumber = formValues.passportNumber;
  }

  await store.dispatchApiAction('UPDATE_USER', payload);

  if (updateUserRequestStatus.value.isError) {
    showErrorMessage.value = true;
    errorMessage.value =
      updateUserRequestStatus.value.errorMessage ||
      'An error occurred while updating your profile.';

    analytics.track(SEGMENT_EVENTS.clear_pay_error, {
      errorMessage: errorMessage.value,
    });

    return false;
  }

  profiling.finish();
  return true;
};
</script>

<template>
  <div class="verification-page">
    <h3 class="page-title">Additional Information</h3>

    <form class="c-form">
      <div class="c-form__row grid grid-cols-12">
        <div class="col-span-4 md:col-span-12">
          <DropdownGroup
            filter
            reset-filter-on-hide
            name="citizenship"
            label="Country of Citizenship"
            option-label="nameString"
            option-value="value"
            :options="COUNTRIES"
            :placeholder="$t('common.selectOption')"
            append-to="self"
            @change="onCitizenshipChange"
          />
        </div>
        <div class="col-span-4 md:col-span-12">
          <div
            v-if="
              values?.citizenship &&
              citizenshipIsTaxIdCountry(values.citizenship)
            "
          >
            <InputTextGroup
              v-only-numbers
              name="personalTaxId"
              :label="personalTaxIdLabelShort(values?.citizenship)"
              :use-grouping="false"
            />
          </div>
          <div v-else>
            <InputTextGroup
              name="passportNumber"
              label="Passport Number"
              :use-grouping="false"
            />
          </div>
        </div>
        <div class="col-span-4 md:col-span-12">
          <CalendarGroup
            label="Date of Birth"
            name="birthday"
            :max-date="new Date()"
            :manual-input="false"
            placeholder="yyyy-mm-dd"
            show-icon
          />
        </div>
      </div>

      <div class="c-form__row">
        <AutoCompleteAddress
          name="line1"
          label="Home Address"
          @addressSelected="
            (place: PlaceDataOutput) => handleAddressSelected(place)
          "
        />
      </div>

      <div class="c-form__row">
        <InputTextGroup name="line2" label="Home Address Line 2" />
      </div>

      <div class="c-form__row grid grid-cols-12">
        <div class="col-span-6 md:col-span-12">
          <DropdownGroup
            name="country"
            label="Country"
            filter
            reset-filter-on-hide
            option-label="nameString"
            option-value="value"
            :options="COUNTRIES"
            :placeholder="$t('common.selectOption')"
            append-to="self"
          />
        </div>
        <div class="col-span-6 md:col-span-12">
          <InputTextGroup name="city" :label="$t('common.address.city')" />
        </div>
      </div>

      <div class="c-form__row grid grid-cols-12">
        <div
          v-if="stateOptionsForCountry(values.country)"
          class="col-span-6 md:col-span-12"
        >
          <DropdownGroup
            name="state"
            :label="stateLabel(values.country)"
            :filter="true"
            :reset-filter-on-hide="true"
            option-label="nameString"
            option-value="value"
            :options="stateOptionsForCountry(values.country)"
            :placeholder="$t('common.selectOption')"
            append-to="self"
          />
        </div>

        <div class="col-span-6 md:col-span-12">
          <InputTextGroup
            v-if="values.country && isCountryWithPostalCode(values.country)"
            name="postalCode"
            :label="postalCodeLabel(values.country)"
          />
        </div>
      </div>
      <div>
        <OnboardingInvitedButtons :onNext="handleNext" :disabled="hasErrors" />
      </div>
    </form>
  </div>
</template>

<style lang="less" scoped>
.verification-page {
  max-width: 100%;
  margin: 0 auto;
  padding: 20px;
}

.page-title {
  text-align: left;
  font-family: @regular-font;
  font-weight: 600;
  font-size: 20px;
  line-height: 28px;
  letter-spacing: 0px;
}
</style>
